import { Box, createStyles } from '@mantine/core';
import type { Filter } from '@repo/api-codegen';
import { EntityType } from '@repo/common/enums/entityType';
import { useMemo } from 'react';
import { teamsGrantedFilter } from '../../../api';
import {
	fetchCatalogList,
	useCatalogList,
} from '../../../api/hooks/resourceCatalog';
import { useCatalogFilter } from '../../../components/Filter/useCatalogFilter';
import { TableV2 } from '../../../components/TableV2';
import { useDataQualityScoreColumns } from '../../../components/TableV2/SecodaEntity.hooks';
import { useEntityBreadcrumbTeam } from '../../../hooks/useEntityBreadcrumbs';
import { useFeatureFlags } from '../../../utils/featureFlags';
import {
	useActions,
	useColumns,
} from '../../TeamCatalogPage/TeamCatalogPage.hooks';

const WITH_CUSTOM_PROPERTIES = [EntityType.table, EntityType.generic];

interface ITablesTabProps {
	schemaId: string;
}

const useStyles = createStyles({
	tableWrapper: {
		height: '100%',
	},
});

function TablesTab({ schemaId }: ITablesTabProps) {
	const { classes } = useStyles();

	const defaultColumns = useColumns();
	const { teamsPermissionsV2Frontend } = useFeatureFlags();
	const { teamBreadcrumb } = useEntityBreadcrumbTeam();
	const dataQualityColumns = useDataQualityScoreColumns({
		catalogType: EntityType.table,
	});

	const columns = useMemo(
		() => [...defaultColumns, ...dataQualityColumns],
		[dataQualityColumns, defaultColumns]
	);

	const actions = useActions();

	const filterOptions = useCatalogFilter();

	const defaultRequiredCatalogFilters = useMemo(() => {
		const operands: Filter[] = [
			{
				operands: [],
				field: 'parent_id',
				operator: 'exact',
				value: schemaId,
			},
			{
				operands: [],
				field: 'entity_type',
				operator: 'in',
				value: [EntityType.table, EntityType.generic],
			},
		];

		if (teamBreadcrumb && teamsPermissionsV2Frontend) {
			operands.push(teamsGrantedFilter(teamBreadcrumb));
		}

		return { operands };
	}, [schemaId, teamBreadcrumb, teamsPermissionsV2Frontend]);

	return (
		<Box className={classes.tableWrapper}>
			<TableV2
				withCustomProperties={WITH_CUSTOM_PROPERTIES}
				pluralTypeString="tables"
				withSearch
				withInteractiveHeader
				withServerCsvExport
				withCsvExport
				withCheckbox
				withActions={actions}
				withFilterOptions={filterOptions}
				columns={columns}
				fetchPaginationList={fetchCatalogList}
				usePaginationList={useCatalogList}
				columnVisibility={{
					catalogType: EntityType.table,
					catalogServerType: EntityType.table,
				}}
				defaultRequiredCatalogFilters={defaultRequiredCatalogFilters}
			/>
		</Box>
	);
}

export default TablesTab;
